import { useCallback, useEffect, useState } from 'react';
import useUser from './useUser';
import type { UpgradeOfferPlan, UpgradeOfferPresentation } from '../components/UpgradeOfferSummary';

export type UpgradeOffer = UpgradeOfferPresentation & {
  productName: string | null;
  productSlug: string | null;
  checkoutUrl: string | null;
  contactSalesUrl: string | null;
  monthlyAiRetailCreditUsd: string | null;
  resetDate: string;
  plans: UpgradeOfferPlan[];
  terminal: boolean;
};

export default function useUpgradeOffer(enabled = true) {
  const { getApiToken, isAuthenticated, authLoading } = useUser();
  const [offer, setOffer] = useState<UpgradeOffer | null>(null);
  // Start in the loading state when enabled so a prompt shows its skeleton,
  // not a brief "unavailable" flash, before the first request starts.
  const [loading, setLoading] = useState(enabled);
  const [error, setError] = useState('');
  // Whether the account is out of AI credits right now (Node usage.ai.outOfCredits).
  // null while unknown. Lets an old upgrade card stop selling once credits are back.
  const [outOfCredits, setOutOfCredits] = useState<boolean | null>(null);

  const refresh = useCallback(async () => {
    if (authLoading) return;
    if (!enabled || !isAuthenticated) {
      setLoading(false);
      return;
    }
    setLoading(true);
    setError('');
    try {
      const token = await getApiToken();
      const apiBaseUrl = process.env.REACT_APP_API_URL || 'api';
      const response = await fetch(`${apiBaseUrl}/agent/usage-summary`, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });
      const payload = await response.json();
      if (!response.ok) throw new Error(payload?.message || 'Could not load plan options.');
      setOffer(payload?.upgrade || null);
      // Node sends a boolean; anything else (older Node) means unknown.
      const aiOutOfCredits = payload?.usage?.ai?.outOfCredits;
      setOutOfCredits(aiOutOfCredits === true ? true : aiOutOfCredits === false ? false : null);
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : 'Could not load plan options.');
    } finally {
      setLoading(false);
    }
  }, [authLoading, enabled, getApiToken, isAuthenticated]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  return { offer, loading, error, outOfCredits, refresh };
}
